/**
 * Lucide `folders` — the stacked pair, which is what a **shared space** is: a
 * room holding folders, not a folder.
 *
 * **It is one glyph in three places on purpose.** The rail's Files icon, the
 * space card, and the space table row all name the same destination, and the
 * rail drew a single `folder` while the thing it led to was a grid of spaces —
 * so the icon said "folder" one click before the screen said "space", and again
 * one level down where `SpaceRoot`'s own `FolderIcon` genuinely does mean a
 * folder. The two glyphs now carry that distinction: stacked is a space, single
 * is a folder.
 *
 * Hand-copied under the house SVG contract, like every glyph in this workspace —
 * `viewBox="0 0 24 24"`, `fill="none"`, `stroke="currentColor"`, 2px round
 * caps/joins, `aria-hidden`. ADR-0053 says why there is no icon library here.
 *
 * **Flat rather than in a directory, and not in `packages/ui`**, on `CardGrid`'s
 * precedent: it owns no copy, and only this app needs it. Promoting it would
 * mean a required `label` prop — that package hardcodes no English — on a glyph
 * that is decorative every time it is drawn, because the name it sits beside is
 * always right there.
 */
export interface FoldersIconProps {
  /** Edge length in px. 20 on the rail, 18 in a table row; the tile upscales its own. */
  size?: number;
  className?: string;
}

export function FoldersIcon({ size = 18, className }: FoldersIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="M20 17a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-4.5a1 1 0 0 1-.8-.4l-1.4-1.867a1 1 0 0 0-.8-.4H8a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1z" />
      <path d="M2 8v11a1 1 0 0 0 1 1h11" />
    </svg>
  );
}
